import { gql } from '@apollo/client';

/**
 * Watchlist writes.
 *
 * The item writes name the security by symbol AND type — the backend keys on
 * the pair, so a scheme and a portfolio that happen to share a symbol are
 * separate rows — and the LIST it goes on (`listName`; the default list,
 * "General", when omitted). The price limits are optional: a watch with no
 * band is just a follow.
 *
 * Each answers `{ success, message }` rather than the row it wrote, so the
 * caller re-reads the list after a write instead of patching a cache entry it
 * cannot construct.
 *
 * Symbol and type are declared non-null because the schema requires them
 * (`String!`). Declared nullable, every write failed validation on the server
 * before reaching the resolver — the watchlist buttons and the agent's
 * `watchlist_add` / `watchlist_remove` alike.
 */
export const ADD_TO_WATCHLIST = gql`
  mutation AddToWatchlist(
    $securitySymbol: String!
    $securityType: String!
    $upperPriceLimit: Float
    $lowerPriceLimit: Float
    $listName: String
  ) {
    addToWatchlist(
      securitySymbol: $securitySymbol
      securityType: $securityType
      upperPriceLimit: $upperPriceLimit
      lowerPriceLimit: $lowerPriceLimit
      listName: $listName
    ) {
      success
      message
    }
  }
`;

export const UPDATE_WATCHLIST_ITEM = gql`
  mutation UpdateWatchlistItem(
    $securitySymbol: String!
    $securityType: String!
    $upperPriceLimit: Float
    $lowerPriceLimit: Float
    $listName: String
  ) {
    updateWatchlistItem(
      securitySymbol: $securitySymbol
      securityType: $securityType
      upperPriceLimit: $upperPriceLimit
      lowerPriceLimit: $lowerPriceLimit
      listName: $listName
    ) {
      success
      message
    }
  }
`;

/** Without `listName` the item comes off every list it is on. */
export const REMOVE_FROM_WATCHLIST = gql`
  mutation RemoveFromWatchlist($securitySymbol: String!, $securityType: String!, $listName: String) {
    removeFromWatchlist(securitySymbol: $securitySymbol, securityType: $securityType, listName: $listName) {
      success
      message
    }
  }
`;

/* The lists themselves. A list made by saving a name to a new list name
   lasts while something is on it; one created here is kept while empty. */
export const CREATE_WATCHLIST = gql`
  mutation CreateWatchlist($listName: String!) {
    createWatchlist(listName: $listName) { success message }
  }
`;

export const RENAME_WATCHLIST = gql`
  mutation RenameWatchlist($oldName: String!, $newName: String!) {
    renameWatchlist(oldName: $oldName, newName: $newName) { success message }
  }
`;

export const DELETE_WATCHLIST = gql`
  mutation DeleteWatchlist($listName: String!) {
    deleteWatchlist(listName: $listName) { success message }
  }
`;
